// Notification settings for one conversation: mute it for a while or until turned back on, and
// (channels) whether every new message notifies or only mentions and threads you follow. Saved on
// your channelReads record, so they follow you to every device.
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { format, isToday } from 'date-fns';
import type { Channel } from '@shared/types';
import { Bell, BellOff } from '@/components/ui/icons';
import { useDbShallow, useMe } from '@/lib/store';
import { chatPrefsOf, MUTE_FOREVER, setChatPrefs, type ChannelNotify } from '@/lib/actions';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { toast } from '@/components/ui/overlays';

const MINUTE = 60_000;
const MUTE_OPTIONS: { label: string; ms: number | null }[] = [
  { label: '1 minute', ms: MINUTE },
  { label: '5 minutes', ms: 5 * MINUTE },
  { label: '10 minutes', ms: 10 * MINUTE },
  { label: '30 minutes', ms: 30 * MINUTE },
  { label: '1 hour', ms: 60 * MINUTE },
  { label: '5 hours', ms: 300 * MINUTE },
  { label: 'Until I change it', ms: null },
];

/** My settings for a conversation; re-renders when a mute runs out. */
export function useChatPrefs(channel: Channel) {
  const me = useMe();
  const prefs = useDbShallow((db) => chatPrefsOf(db, me, channel));
  const [, expire] = useState(0);
  const until = prefs.mutedUntil;
  useEffect(() => {
    if (!until || until === MUTE_FOREVER) return;
    const t = setTimeout(() => expire((n) => n + 1), Math.min(until - Date.now() + 50, 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [until]);
  return prefs;
}

export function mutedLabel(until: number) {
  if (until >= MUTE_FOREVER) return 'Muted until you turn it back on';
  return `Muted until ${format(until, isToday(until) ? 'h:mm a' : 'MMM d, h:mm a')}`;
}

export function MuteMenu({ channel, close }: { channel: Channel; close: () => void }) {
  const { mutedUntil } = useChatPrefs(channel);
  const mute = (o: (typeof MUTE_OPTIONS)[number]) => {
    setChatPrefs(channel.id, { mutedUntil: o.ms ? Date.now() + o.ms : MUTE_FOREVER });
    close();
    toast(o.ms ? `Muted for ${o.label}` : 'Muted until you turn notifications back on');
  };
  return (
    <Menu style={{ width: 240 }}>
      {mutedUntil && (
        <>
          <MenuLabel>{mutedLabel(mutedUntil)}</MenuLabel>
          <MenuItem icon={<Bell size={15} />} label="Unmute" onClick={() => (setChatPrefs(channel.id, { mutedUntil: null }), close(), toast('Notifications are back on'))} />
          <MenuSeparator />
        </>
      )}
      <MenuLabel>Mute notifications for</MenuLabel>
      {MUTE_OPTIONS.map((o) => (
        <MenuItem key={o.label} label={o.label} onClick={() => mute(o)} />
      ))}
    </Menu>
  );
}

/** The Mute button: a round action in channel settings, or a small tool button on a DM profile. */
export function MuteButton({ channel, variant }: { channel: Channel; variant: 'action' | 'tool' }) {
  const { mutedUntil } = useChatPrefs(channel);
  const label = mutedUntil ? mutedLabel(mutedUntil) : 'Mute notifications';
  const icon = mutedUntil ? <BellOff size={variant === 'action' ? 17 : 15} /> : <Bell size={variant === 'action' ? 17 : 15} />;
  return (
    <Popover placement="bottom" content={(close) => <MuteMenu channel={channel} close={close} />}>
      {variant === 'action' ? (
        <button type="button" className={clsx('chs-action', { on: !!mutedUntil })} title={label}>
          <span className="chs-action-btn">{icon}</span>
          {mutedUntil ? 'Muted' : 'Mute'}
        </button>
      ) : (
        <button type="button" className={clsx('chp-tool', { on: !!mutedUntil })} title={label} aria-label={label}>
          {icon}
        </button>
      )}
    </Popover>
  );
}

const NOTIFY_CHOICES: { key: ChannelNotify; label: string; hint: (name: string) => string }[] = [
  { key: 'all', label: 'All new messages', hint: (name) => `Every message posted in #${name}, and replies in threads you follow.` },
  { key: 'mentions', label: 'Mentions and threads', hint: () => 'When you’re @mentioned, and replies in threads you started, replied in or were mentioned in.' },
];

/** Channel settings card: what notifies you here, and whether it's muted. */
export function NotifyCard({ channel }: { channel: Channel }) {
  const { notify, mutedUntil } = useChatPrefs(channel);
  return (
    <section className="chs-card">
      <div className="chs-label">Notify me about</div>
      <div className="chs-notify" role="radiogroup" aria-label="Notify me about">
        {NOTIFY_CHOICES.map((c) => (
          <button key={c.key} type="button" role="radio" aria-checked={notify === c.key} className="chs-notify-opt" onClick={() => setChatPrefs(channel.id, { notify: c.key })}>
            <span className="chs-radio" data-on={notify === c.key} />
            <span className="chs-option-text">
              <span>{c.label}</span>
              <span className="chs-option-hint">{c.hint(channel.name)}</span>
            </span>
          </button>
        ))}
      </div>
      {mutedUntil && (
        <div className="chs-muted-note">
          <BellOff size={14} /> {mutedLabel(mutedUntil)}
          <button type="button" className="chs-link" onClick={() => setChatPrefs(channel.id, { mutedUntil: null })}>
            Unmute
          </button>
        </div>
      )}
    </section>
  );
}
